<template>
  <div class="user-analysis-wrap">
    <main class="page">

      <template v-if="!embedded">
        <p class="breadcrumb">Swiss Eating Quiz / Internal / Dashboard</p>

        <h1 class="page-title">Participation Dashboard</h1>
        <p class="page-subtitle">
          Aggregate participation patterns across cantons and over time. Internal
          use only, not visible to participants.
        </p>

        <div class="meta-strip">
          <div v-for="m in meta" :key="m.label" class="meta-item">
            <span class="meta-label">{{ m.label }}</span>
            <span class="meta-value">{{ m.value }}</span>
          </div>
        </div>

        <section class="section">
          <h2 class="section-title">Summary</h2>

          <div class="stats-grid">
            <div v-for="s in stats" :key="s.label" class="stat-card">
              <p class="stat-card-label">{{ s.label }}</p>
              <p class="stat-card-value">{{ s.value }}</p>
              <p class="stat-card-trend" :class="{ up: s.up }">{{ s.trend }}</p>
            </div>
          </div>
        </section>
      </template>

      <section class="section">
        <h2 class="section-title">Distribution across Switzerland</h2>
        <p class="section-text">
          Each dot represents one canton, sized by the number of submissions
          originating from that canton. Locations are derived from IP addresses
          at the time of submission. The IP itself is not stored.
        </p>

        <div class="map-row">
          <div class="map-card">
            <p class="map-card-title">Submissions per canton</p>
            <p class="map-card-sub">Dot size proportional to submission count.</p>

            <svg class="map-svg" viewBox="0 0 600 380" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M 60 200 Q 80 140 130 130 Q 180 110 240 120 Q 290 105 340 115 Q 400 110 460 130 Q 510 140 540 170 Q 555 200 540 230 Q 525 260 480 275 Q 430 290 380 280 Q 340 295 290 285 Q 240 295 190 280 Q 130 270 95 250 Q 65 230 60 200 Z"
                fill="rgba(83, 74, 183, 0.06)"
                stroke="rgba(83, 74, 183, 0.4)"
                stroke-width="1"
                stroke-dasharray="2,3"
              />

              <template v-for="d in mapDots" :key="d.code || `${d.cx}-${d.cy}`">
                <circle :cx="d.cx" :cy="d.cy" :r="d.r" fill="#534AB7" opacity="0.65"/>
                <text
                  v-if="d.code"
                  :x="d.cx"
                  :y="d.cy + 5"
                  text-anchor="middle"
                  font-family="Montserrat, sans-serif"
                  :font-size="d.fontSize"
                  font-weight="600"
                  fill="white"
                >{{ d.code }}</text>
              </template>
            </svg>

            <div class="map-legend">
              <span>Dot size:</span>
              <span class="legend-dot" style="width: 8px; height: 8px;"></span>
              <span>~50</span>
              <span class="legend-dot" style="width: 16px; height: 16px;"></span>
              <span>~500</span>
              <span class="legend-dot" style="width: 28px; height: 28px;"></span>
              <span>~2000</span>
            </div>
          </div>

          <div class="map-card">
            <p class="map-card-title">Canton breakdown</p>
            <p class="map-card-sub">Top 12 cantons by submission count.</p>

            <table class="canton-table">
              <thead>
                <tr>
                  <th>Canton</th>
                  <th class="right">Count</th>
                  <th class="right">%</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in cantons" :key="c.name">
                  <td>
                    <span class="canton-bar" :style="{ width: c.barWidth + 'px' }"></span>{{ c.name }}
                  </td>
                  <td class="right">{{ c.count }}</td>
                  <td class="right">{{ c.pct }}</td>
                </tr>
                <tr><td colspan="3" class="canton-other">14 other cantons: 1 536 (12.0%)</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">When users take the quiz</h2>
        <p class="section-text">
          Submission start times across the day, aggregated over the last 30
          days. Useful for planning announcements, server scaling, and
          identifying when the quiz is most often used in classroom settings.
        </p>

        <div class="time-row">
          <div class="map-card">
            <p class="map-card-title">By hour of day</p>
            <p class="map-card-sub">Quiz starts, all days. Two clear peaks: lunchtime and evening.</p>

            <svg class="chart-svg" viewBox="0 0 540 220" xmlns="http://www.w3.org/2000/svg">
              <g stroke="rgba(0,0,0,0.06)" stroke-width="1">
                <line x1="40" y1="40" x2="520" y2="40"/>
                <line x1="40" y1="80" x2="520" y2="80"/>
                <line x1="40" y1="120" x2="520" y2="120"/>
                <line x1="40" y1="160" x2="520" y2="160"/>
              </g>

              <g font-family="Montserrat, sans-serif" font-size="9" fill="#888780">
                <text x="35" y="44" text-anchor="end">200</text>
                <text x="35" y="84" text-anchor="end">150</text>
                <text x="35" y="124" text-anchor="end">100</text>
                <text x="35" y="164" text-anchor="end">50</text>
                <text x="35" y="194" text-anchor="end">0</text>
              </g>

              <g fill="#534AB7">
                <rect
                  v-for="(h, i) in hourBars"
                  :key="`hb-${i}`"
                  :x="44 + i * 20"
                  :y="190 - h"
                  width="16"
                  :height="h"
                  rx="2"
                />
              </g>

              <g font-family="Montserrat, sans-serif" font-size="9" fill="#888780">
                <text x="52" y="205" text-anchor="middle">00</text>
                <text x="132" y="205" text-anchor="middle">04</text>
                <text x="212" y="205" text-anchor="middle">08</text>
                <text x="292" y="205" text-anchor="middle">12</text>
                <text x="372" y="205" text-anchor="middle">16</text>
                <text x="452" y="205" text-anchor="middle">20</text>
                <text x="512" y="205" text-anchor="middle">23</text>
              </g>

              <g font-family="Montserrat, sans-serif" font-size="10" fill="#3F368F" font-weight="600">
                <text x="272" y="52" text-anchor="middle">12:00 lunch</text>
                <text x="432" y="32" text-anchor="middle">20:00 evening peak</text>
              </g>
            </svg>
          </div>

          <div class="map-card">
            <p class="map-card-title">By day of week</p>
            <p class="map-card-sub">Mondays and Wednesdays lead. Sunday is the quietest day.</p>

            <svg class="chart-svg" viewBox="0 0 540 220" xmlns="http://www.w3.org/2000/svg">
              <g stroke="rgba(0,0,0,0.06)" stroke-width="1">
                <line x1="40" y1="40" x2="520" y2="40"/>
                <line x1="40" y1="80" x2="520" y2="80"/>
                <line x1="40" y1="120" x2="520" y2="120"/>
                <line x1="40" y1="160" x2="520" y2="160"/>
              </g>

              <g font-family="Montserrat, sans-serif" font-size="9" fill="#888780">
                <text x="35" y="44" text-anchor="end">2 500</text>
                <text x="35" y="84" text-anchor="end">2 000</text>
                <text x="35" y="124" text-anchor="end">1 500</text>
                <text x="35" y="164" text-anchor="end">1 000</text>
                <text x="35" y="194" text-anchor="end">0</text>
              </g>

              <g fill="#534AB7">
                <rect x="60" y="48" width="55" height="142" rx="2"/>
                <rect x="130" y="68" width="55" height="122" rx="2"/>
                <rect x="200" y="52" width="55" height="138" rx="2"/>
                <rect x="270" y="76" width="55" height="114" rx="2"/>
                <rect x="340" y="98" width="55" height="92" rx="2"/>
                <rect x="410" y="138" width="55" height="52" rx="2"/>
                <rect x="480" y="148" width="35" height="42" rx="2"/>
              </g>

              <g font-family="Montserrat, sans-serif" font-size="10" fill="#888780">
                <text x="87" y="205" text-anchor="middle">Mon</text>
                <text x="157" y="205" text-anchor="middle">Tue</text>
                <text x="227" y="205" text-anchor="middle">Wed</text>
                <text x="297" y="205" text-anchor="middle">Thu</text>
                <text x="367" y="205" text-anchor="middle">Fri</text>
                <text x="437" y="205" text-anchor="middle">Sat</text>
                <text x="497" y="205" text-anchor="middle">Sun</text>
              </g>
            </svg>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Outside Switzerland</h2>
        <p class="section-text">
          A small share of submissions come from IP addresses outside
          Switzerland. Most are likely Swiss expatriates and travellers; a
          smaller fraction may be VPN traffic. These are kept in the dataset
          but marked as international.
        </p>

        <div class="map-card">
          <table class="international-table">
            <thead>
              <tr>
                <th>Country</th>
                <th class="right">Count</th>
                <th class="right">% of total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in international" :key="row.country" :class="{ total: row.total }">
                <td>{{ row.country }}</td>
                <td class="right">{{ row.count }}</td>
                <td class="right">{{ row.pct }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div class="footer">
        <span>Internal use only · BFH Departement Gesundheit</span>
        <span>Mock data · v0.1 · 9 May 2026</span>
      </div>

    </main>
  </div>
</template>

<script>
export default {
  name: 'UserAnalysis',
  // Every figure in this component is invented. It was built to show the
  // team what a participation dashboard could look like, and the features
  // behind it (canton lookup, timing analysis) are not implemented.
  //
  // `embedded` drops its page shell and its fake summary tiles so the real
  // results page can mount it below a "To be implemented" heading, rather
  // than copying two hundred lines of mockup SVG into a second file and
  // creating exactly the kind of duplicate that goes stale.
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      meta: [
        { label: 'Period', value: 'Last 30 days' },
        { label: 'Source', value: 'submission_locations table' },
        { label: 'Geo lookup', value: 'MaxMind GeoLite2, refreshed monthly' },
        { label: 'Last updated', value: '9 May 2026, 14:32' },
      ],
      stats: [
        { label: 'Total submissions', value: '12 847', trend: '+428 in last 7 days', up: true },
        { label: 'Today', value: '73', trend: 'vs. 68 yesterday', up: false },
        { label: 'Cantons reached', value: '26 / 26', trend: 'All cantons covered', up: false },
        { label: 'Peak hour', value: '20:00', trend: 'Weekday evenings', up: false },
      ],
      mapDots: [
        { cx: 380, cy: 170, r: 32, code: 'ZH', fontSize: 12 },
        { cx: 240, cy: 200, r: 28, code: 'BE', fontSize: 11 },
        { cx: 130, cy: 240, r: 22, code: 'VD', fontSize: 10 },
        { cx: 320, cy: 160, r: 18, code: 'AG', fontSize: 10 },
        { cx: 470, cy: 170, r: 16, code: 'SG', fontSize: 10 },
        { cx: 320, cy: 200, r: 15, code: 'LU', fontSize: 10 },
        { cx: 380, cy: 280, r: 14, code: 'TI', fontSize: 10 },
        { cx: 240, cy: 130, r: 13, code: 'BS', fontSize: 9 },
        { cx: 80, cy: 270, r: 12, code: 'GE', fontSize: 9 },
        { cx: 180, cy: 220, r: 11, code: 'FR', fontSize: 9 },
        { cx: 220, cy: 270, r: 11, code: 'VS', fontSize: 9 },
        { cx: 270, cy: 160, r: 9 },
        { cx: 430, cy: 140, r: 9 },
        { cx: 265, cy: 135, r: 8 },
        { cx: 350, cy: 220, r: 7 },
        { cx: 290, cy: 240, r: 7 },
        { cx: 410, cy: 200, r: 7 },
        { cx: 450, cy: 220, r: 6 },
        { cx: 380, cy: 240, r: 6 },
        { cx: 500, cy: 190, r: 5 },
        { cx: 170, cy: 270, r: 5 },
        { cx: 290, cy: 180, r: 5 },
        { cx: 345, cy: 175, r: 4 },
        { cx: 455, cy: 190, r: 4 },
        { cx: 475, cy: 200, r: 4 },
        { cx: 465, cy: 155, r: 4 },
      ],
      cantons: [
        { name: 'Zürich (ZH)',       count: '2 814', pct: '21.9%', barWidth: 80 },
        { name: 'Bern (BE)',         count: '2 211', pct: '17.2%', barWidth: 64 },
        { name: 'Vaud (VD)',         count: '1 462', pct: '11.4%', barWidth: 42 },
        { name: 'Aargau (AG)',       count: '983',   pct: '7.7%',  barWidth: 28 },
        { name: 'St. Gallen (SG)',   count: '762',   pct: '5.9%',  barWidth: 22 },
        { name: 'Luzern (LU)',       count: '654',   pct: '5.1%',  barWidth: 19 },
        { name: 'Ticino (TI)',       count: '541',   pct: '4.2%',  barWidth: 16 },
        { name: 'Basel-Stadt (BS)',  count: '487',   pct: '3.8%',  barWidth: 14 },
        { name: 'Genève (GE)',       count: '416',   pct: '3.2%',  barWidth: 12 },
        { name: 'Fribourg (FR)',     count: '359',   pct: '2.8%',  barWidth: 10 },
        { name: 'Valais (VS)',       count: '348',   pct: '2.7%',  barWidth: 10 },
        { name: 'Solothurn (SO)',    count: '274',   pct: '2.1%',  barWidth: 8 },
      ],
      // Heights tuned for a Swiss daily rhythm: low overnight, lunch peak around 12:00, evening peak around 20:00.
      hourBars: [12, 6, 4, 6, 10, 24, 48, 70, 82, 92, 116, 130, 98, 74, 58, 66, 84, 106, 132, 150, 134, 98, 52, 24],
      international: [
        { country: 'Germany',              count: '87',  pct: '0.68%' },
        { country: 'France',               count: '62',  pct: '0.48%' },
        { country: 'Italy',                count: '41',  pct: '0.32%' },
        { country: 'Austria',              count: '28',  pct: '0.22%' },
        { country: 'United Kingdom',       count: '19',  pct: '0.15%' },
        { country: 'United States',        count: '14',  pct: '0.11%' },
        { country: 'Other (24 countries)', count: '52',  pct: '0.40%' },
        { country: 'Total international',  count: '303', pct: '2.36%', total: true },
      ],
    };
  },
};
</script>

<style scoped>
.user-analysis-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-good: #2D8659;
  --color-warn: #E8A33D;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.user-analysis-wrap *,
.user-analysis-wrap *::before,
.user-analysis-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0 0 2rem;
  font-weight: 400;
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 13px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

.section-text {
  font-size: 15px;
  color: var(--color-text-primary);
  line-height: 1.7;
  margin: 0 0 1rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 2.5rem;
}

.stat-card {
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  padding: 18px 20px;
}

.stat-card-label {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  margin: 0 0 8px;
}

.stat-card-value {
  font-size: 28px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.1;
}

.stat-card-trend {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 6px 0 0;
}

.stat-card-trend.up { color: var(--color-good); }

.map-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 20px;
  align-items: start;
}

.map-card {
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 20px;
}

.map-card-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 4px;
}

.map-card-sub {
  font-size: 12px;
  color: var(--color-text-tertiary);
  margin: 0 0 16px;
}

.map-svg {
  width: 100%;
  height: auto;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
}

.map-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 11px;
  color: var(--color-text-tertiary);
}

.legend-dot {
  display: inline-block;
  border-radius: 50%;
  background: var(--color-accent);
  opacity: 0.7;
}

.canton-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.canton-table th {
  text-align: left;
  font-weight: 600;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  padding: 10px 12px;
  border-bottom: 0.5px solid var(--color-border);
}

.canton-table th.right { text-align: right; }

.canton-table td {
  padding: 10px 12px;
  border-bottom: 0.5px solid var(--color-border);
  color: var(--color-text-primary);
}

.canton-table td.right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.canton-table tr:last-child td { border-bottom: none; }

.canton-other {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.canton-bar {
  display: inline-block;
  height: 6px;
  background: var(--color-accent);
  border-radius: 3px;
  margin-right: 8px;
  vertical-align: middle;
}

.time-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.chart-svg {
  width: 100%;
  height: 220px;
}

.international-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.international-table th,
.international-table td {
  padding: 8px 12px;
  border-bottom: 0.5px solid var(--color-border);
}

.international-table th {
  text-align: left;
  font-weight: 600;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}

.international-table th.right,
.international-table td.right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.international-table tr.total {
  font-weight: 600;
}

.footer {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 0.5px solid var(--color-border);
  font-size: 12px;
  color: var(--color-text-tertiary);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

@media (max-width: 900px) {
  .map-row,
  .time-row { grid-template-columns: 1fr; }
}

@media (max-width: 800px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
